import { AuroraMesh, Button, OrbVisualizer } from '@qi/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { authClient } from '../lib/auth-client.ts';
import { useUpdateProfile } from '../lib/queries.ts';
import { useSessionStore } from '../stores/session-store.ts';

type AuthMode = 'signin' | 'signup';

export interface AuthSearch {
  mode?: AuthMode;
}

export const Route = createFileRoute('/_marketing/auth')({
  component: AuthPage,
  validateSearch: (search: Record<string, unknown>): AuthSearch => {
    const raw = search.mode;
    if (raw === 'signin' || raw === 'signup') return { mode: raw };
    return {};
  },
});

const COPY = {
  signin: {
    eyebrow: 'Sign in',
    headline: 'Welcome back',
    body: 'Pick up where you left off. Sign in to keep your scan history and protocols.',
    cta: 'Sign in',
    switchText: 'New to Qi Scan?',
    switchCta: 'Create an account',
    switchTo: 'signup' as const,
  },
  signup: {
    eyebrow: 'Create account',
    headline: 'Make this yours',
    body: 'Create an account to save scans across devices. Or continue as a guest — your scans stay on this browser.',
    cta: 'Create account',
    switchText: 'Already have an account?',
    switchCta: 'Sign in',
    switchTo: 'signin' as const,
  },
} as const satisfies Record<
  AuthMode,
  {
    eyebrow: string;
    headline: string;
    body: string;
    cta: string;
    switchText: string;
    switchCta: string;
    switchTo: AuthMode;
  }
>;

function AuthPage() {
  const { mode = 'signin' } = Route.useSearch();
  const navigate = useNavigate();
  const localSignIn = useSessionStore((s) => s.signIn);
  const onboardingGoal = useSessionStore((s) => s.goal);
  const updateProfile = useUpdateProfile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[mode];

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Enter a valid email to continue');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        const res = await authClient.signUp.email({
          email: trimmedEmail,
          password,
          name: trimmedEmail.split('@')[0] ?? 'Qi Scan friend',
        });
        if (res.error) throw new Error(res.error.message ?? 'Sign-up failed');
        // Persist the goal chosen during onboarding to the new profile. The
        // store value is local-only otherwise, so it would be lost on another
        // device. Non-fatal: a failure here shouldn't block sign-up.
        if (onboardingGoal) {
          try {
            await updateProfile.mutateAsync({ goal: onboardingGoal });
          } catch {
            // ignore — the goal stays in the local store; Settings can re-save it
          }
        }
      } else {
        const res = await authClient.signIn.email({ email: trimmedEmail, password });
        if (res.error) throw new Error(res.error.message ?? 'Sign-in failed');
      }
      // Optimistic store update so the UI flips immediately; AuthSync will reconcile.
      localSignIn({ email: trimmedEmail });
      navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  async function continueAsGuest() {
    setError(null);
    setSubmitting(true);
    try {
      const res = await authClient.signIn.anonymous();
      if (res.error) throw new Error(res.error.message ?? 'Could not start guest session');
      localSignIn({ email: '(guest)' });
      navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  async function continueWithGoogle() {
    setError(null);
    setSubmitting(true);
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: '/dashboard',
      });
      // signIn.social redirects, no need to update store here.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in unavailable');
      setSubmitting(false);
    }
  }

  return (
    <section className="relative overflow-hidden">
      <AuroraMesh intensity="subtle" position="center" />
      <div className="relative mx-auto max-w-md px-6 py-12">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <OrbVisualizer size={88} rings={3} />
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">{copy.eyebrow}</p>
          <h1 className="text-balance font-display text-5xl text-ink-0">{copy.headline}</h1>
          <p className="max-w-sm text-pretty text-ink-1">{copy.body}</p>
        </div>

        <div className="qi-glass rounded-lg p-8" style={{ boxShadow: 'var(--glow-aurora)' }}>
          <form className="space-y-5" onSubmit={submitEmail}>
            <label className="block" htmlFor="auth-email">
              <span className="mb-2 block font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                Email
              </span>
              <input
                id="auth-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                spellCheck={false}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="you@somewhere.com"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'auth-error' : undefined}
                className="w-full rounded-sm border border-hairline bg-surface-3 px-4 py-3 text-sm text-ink-0 placeholder:text-ink-3 focus:border-aurora/60 focus:outline-none focus:ring-4 focus:ring-aurora/15"
              />
            </label>
            <label className="block" htmlFor="auth-password">
              <span className="mb-2 block font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                Password
              </span>
              <input
                id="auth-password"
                name="password"
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="At least 8 characters"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'auth-error' : undefined}
                className="w-full rounded-sm border border-hairline bg-surface-3 px-4 py-3 text-sm text-ink-0 placeholder:text-ink-3 focus:border-aurora/60 focus:outline-none focus:ring-4 focus:ring-aurora/15"
              />
            </label>
            {error && (
              <span id="auth-error" role="alert" className="block text-xs text-rose">
                {error}
              </span>
            )}
            <Button
              type="submit"
              variant="resonance"
              size="lg"
              magnetic
              className="group w-full"
              disabled={submitting}
              aria-busy={submitting}
            >
              <span>{submitting ? 'Continuing…' : copy.cta}</span>
              <span
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              >
                →
              </span>
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-ink-3">
            <span className="h-px flex-1 bg-hairline" />
            or
            <span className="h-px flex-1 bg-hairline" />
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="w-full"
              onClick={continueWithGoogle}
              disabled={submitting}
            >
              Continue with Google
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="w-full"
              onClick={continueAsGuest}
              disabled={submitting}
            >
              Continue as guest
            </Button>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-ink-2">
          {copy.switchText}{' '}
          <Link
            to="/auth"
            search={{ mode: copy.switchTo }}
            className="text-aurora hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/60"
          >
            {copy.switchCta}
          </Link>
        </p>
      </div>
    </section>
  );
}
